import React, { PureComponent } from 'react';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';

import ChartComponent from 'react-chartjs-2';

import { AgolService } from '../../services/Agol';
import { Loading } from '../Loading/Loading';
import { FormatterUtils } from '../../utils/formatter.utils';

import { baseColors } from '../../constants/colors.constant';

import CustomTooltip from '../CustomTooltip/CustomTooltip';

import './CardChart.scss';

export interface CardChartProps {
  codMun?: any;
  category?: any;
}

export interface CardChartState {
  loadingVisible: boolean;
  tabs: any[];
}

export class CardChart extends PureComponent<CardChartProps, CardChartState> {
  private requestId = 0;

  constructor(props) {
    super(props);
    this.state = {
      loadingVisible: false,
      tabs: [],
    };
  }

  componentDidMount() {
    this.fetchData(++this.requestId);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.codMun !== prevProps.codMun ||
      this.props.category !== prevProps.category
    ) {
      this.fetchData(++this.requestId);
    }
  }

  componentWillUnmount() {
    this.requestId = -1;
  }

  isLink(value) {
    value = value || '';
    return !!(value && value.startsWith('http'));
  }

  render() {
    return (
      <>
        <div className='customCardChart'>
          <Loading visible={this.state.loadingVisible} />
          {this.state.loadingVisible ? '' : (
            <div className='cardChartValues'>
              <Tabs id='cardChartTabs' className='cardChartTabsTitle'>
                {this.state.tabs.map((tab) => {
                  return (
                    <Tab
                      key={tab.key}
                      eventKey={tab.key}
                      className='cardChartTab'
                      title={tab.title}
                    >
                      <CustomTooltip
                        disabled={!tab.descricao}
                        tooltip={tab.descricao}
                      >
                        <div className='chart-container'>
                          <ChartComponent
                            type={tab.chart.type}
                            data={tab.chart.data}
                            options={tab.chart.options}
                          />
                        </div>
                      </CustomTooltip>
                      {tab.font ? (
                        <div className='d-flex justify-content-end full-width'>
                          <span className='margin-gutter-single-right'>
                            Fonte:
                          </span>
                          <a
                            href={tab.link || '#'}
                            target='_blank'
                            className={
                              'link' +
                              (!this.isLink(tab.link) ? ' disabled' : '')
                            }
                          >
                            {tab.font}
                          </a>
                        </div>
                      ) : null}
                    </Tab>
                  );
                })}
              </Tabs>
            </div>
          )}
        </div>
      </>
    );
  }

  fetchData(requestId) {
    if (
      this.requestId !== requestId ||
      !this.props.codMun ||
      !this.props.category
    ) {
      if (this.state.loadingVisible) {
        this.setState({ loadingVisible: false });
      }
      return;
    }

    this.setState({ loadingVisible: true });
    return AgolService.getCovidInfoChart(this.props.category)
      .then(({ data }) => {
        if (this.requestId !== requestId) {
          return;
        }
        let promises = data.features.map(({ attributes }, tabIndex) => {
          return AgolService.getData({
            camada: attributes.camada,
            cod_ligacao: attributes.cod_ligacao,
            value: this.props.codMun,
            order_by: attributes.campo_eixox_grafico_principal,
          })
            .then((res) => {
              if (this.requestId !== requestId) {
                return;
              }

              const labels = res.data.features.map((x) => {
                return FormatterUtils.formatDate(
                  x.attributes[attributes.campo_eixox_grafico_principal]
                );
              });

              let datasets: any[] = [];
              let chartType;

              [
                [
                  attributes.tipo_grafico_secundario,
                  attributes.campo_eixoy_grafico_secundario,
                  attributes.label_eixoy_grafico_secundario,
                  attributes.cor_grafico_secundario ?? baseColors.CHART_SECONDARY,
                ],
                [
                  attributes.tipo_grafico_principal,
                  attributes.campo_eixoy_grafico_principal,
                  attributes.label_eixoy_grafico_principal,
                  attributes.cor_grafico_principal ?? baseColors.CHART_PRIMARY,
                ],
              ].forEach(([tipoGrafico, campoGrafico, label, color], index) => {
                const chartTypeAux = this.handleChartType(tipoGrafico);
                if (chartTypeAux) {
                  if (!chartType) {
                    chartType = chartTypeAux;
                  }

                  const chartCfg: any = {
                    label: label,
                    type: chartTypeAux,
                    data: res.data.features.map((x) => {
                      return x.attributes[campoGrafico];
                    }),
                  };
                  if (chartCfg.type === 'bar') {
                    chartCfg.backgroundColor = color;
                  } else if (chartCfg.type === 'line') {
                    chartCfg.borderColor = color;
                    chartCfg.backgroundColor = color;
                    chartCfg.fill = false;
                    chartCfg.pointRadius = 1;
                    chartCfg.pointHoverRadius = 5;
                  }
                  datasets.push(chartCfg);
                }
              });

              chartType = chartType || 'bar';
              return {
                key: `card-chart-${chartType}-${tabIndex}`,
                title: attributes.nome_indicador,
                descricao: attributes.descricao,
                font: attributes.fonte,
                link: attributes.link,
                chart: {
                  type: chartType,
                  data: { datasets, labels },
                  options: {
                    title: {
                      display: true,
                      text: attributes.nome_indicador,
                      fontSize: 10,
                    },
                    legend: {
                      display: true,
                      position: 'bottom',
                    },
                    scales: {
                      display: true,
                      yAxes: [
                        {
                          type: 'linear',
                          display: true,
                          scaleLabel: {
                            display: true,
                            labelString:
                              attributes.label_eixoy_grafico_principal,
                          },
                          ticks: {
                            beginAtZero: true,
                          },
                        },
                      ],
                      xAxes: [
                        {
                          type: 'category',
                          display: true,
                          scaleLabel: {
                            display: true,
                            labelString:
                              attributes.label_eixox_grafico_principal,
                          },
                        },
                      ],
                    },
                    tooltips: {
                      callbacks: {
                        label: (tooltipItem, data) => {
                          let label =
                            data.datasets[tooltipItem.datasetIndex].label || '';

                          if (label) {
                            label += ': ';
                          }
                          label += FormatterUtils.formatNumber(
                            tooltipItem.yLabel
                          );
                          return label;
                        },
                      },
                    },
                    maintainAspectRatio: false,
                  },
                },
              };
            })
            .catch((err) => {
              console.error(err);
            });
        });

        return Promise.all(promises).then((results) => {
          if (this.requestId !== requestId) {
            return;
          }
          this.setState({ tabs: results.filter((x) => !!x) });
        });
      })
      .then(() => {
        if (this.requestId !== requestId) {
          return;
        }
        this.setState({ loadingVisible: false });
      })
      .catch((err) => {
        console.error(err);
        if (this.requestId !== requestId) {
          return;
        }
        this.setState({ loadingVisible: false });
      });
  }

  handleChartType(value) {
    switch (value ? value.toLowerCase() : '') {
      case 'colunas':
        return 'bar';
      case 'linha':
        return 'line';
      default:
        return null;
    }
  }
}
